import React from 'react';
import useTransactionHistory from '../hooks/useTransactionHistory';

const TransactionHistoryPage = () => {
  const { transactions, loading, pagination, error, fetchTransactions } = useTransactionHistory('user');
  const [filters, setFilters] = React.useState({});

  React.useEffect(() => {
    fetchTransactions(1, filters);
  }, [filters]);

  const formatAmount = (amount, direction) => {
    const sign = direction === 'credit' ? '+' : '-';
    const color = direction === 'credit' ? 'text-green-600' : 'text-red-600';
    return (
      <span className={color}>
        {sign}₦{amount.toLocaleString()}
      </span>
    );
  };

  const getTransactionIcon = (type) => {
    const icons = {
      'wallet_funding': '💳',
      'order_payment': '🍕',
      'order_refund': '↩️',
      'transfer_sent': '📤',
      'transfer_received': '📥',
      'scheduled_order_fee': '⏰',
      'group_order_payment': '👥'
    };
    return icons[type] || '💸';
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6"></h1>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <select 
            value={filters.type || ''} 
            onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value || undefined }))}
            className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-gray-900 text-sm focus:ring-primary-500 focus:border-primary-500 bg-white"
          >
            <option value="">All Types</option>
            <option value="wallet_funding">Wallet Funding</option>
            <option value="order_payment">Order Payment</option>
            <option value="order_refund">Refund</option>
            <option value="transfer_sent">Transfer Sent</option>
            <option value="transfer_received">Transfer Received</option>
          </select>

          <select 
            value={filters.direction || ''} 
            onChange={(e) => setFilters(prev => ({ ...prev, direction: e.target.value || undefined }))}
            className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-gray-900 text-sm focus:ring-primary-500 focus:border-primary-500 bg-white"
          >
            <option value="">All Directions</option>
            <option value="credit">Credit</option>
            <option value="debit">Debit</option>
          </select>

          <div className="relative">
            <input
              type="date"
              value={filters.startDate || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value || undefined }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-gray-900 text-sm focus:ring-primary-500 focus:border-primary-500 bg-white placeholder:text-gray-500"
              placeholder="Start Date"
            />
            {!filters.startDate && (
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-500 text-sm">
                Start Date
              </div>
            )}
          </div>

          <div className="relative">
            <input
              type="date"
              value={filters.endDate || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value || undefined }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-gray-900 text-sm focus:ring-primary-500 focus:border-primary-500 bg-white placeholder:text-gray-500"
              placeholder="End Date"
            />
            {!filters.endDate && (
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-500 text-sm">
                End Date
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">Error: {error}</p>
          <button 
            onClick={() => fetchTransactions(1, filters)}
            className="mt-2 text-red-600 hover:text-red-800 underline"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Transaction List */}
      {!loading && !error && (
        <div className="bg-white rounded-lg shadow-sm border">
          {transactions.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No transactions found
            </div>
          ) : (
            <div className="divide-y">
              {transactions.map((transaction) => (
                <div key={transaction._id} className="p-4 hover:bg-gray-50 transition duration-150">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{getTransactionIcon(transaction.type)}</span>
                      <div>
                        <p className="font-medium text-gray-900">
                          {transaction.description}
                        </p>
                        <p className="text-sm text-gray-500">
                          {transaction.transactionId} • {new Date(transaction.createdAt).toLocaleDateString()}
                        </p>
                        {transaction.metadata?.orderCustomId && (
                          <p className="text-xs text-primary-600">
                            Order: {transaction.metadata.orderCustomId}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">
                        {formatAmount(transaction.amount, transaction.direction)}
                      </p>
                      <p className="text-sm text-gray-500">
                        Balance: ₦{transaction.balanceAfter.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => fetchTransactions(pagination.page - 1, filters)}
                  disabled={!pagination.hasPrev}
                  className="px-3 py-1 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  Previous
                </button>
                <span className="px-3 py-1 bg-primary-100 text-primary-800 rounded-md">
                  {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => fetchTransactions(pagination.page + 1, filters)}
                  disabled={!pagination.hasNext}
                  className="px-3 py-1 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TransactionHistoryPage;